import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export interface NumberConstraints {
  min_value?: number | null;
  max_value?: number | null;
  decimal_places?: number | null;
  max_digits?: number | null;
}

export const NUMERIC_FIELD_CONSTRAINTS: Record<string, NumberConstraints> = {
  numberOfEmployees: { min_value: 0, decimal_places: 0, max_digits: 10 },
  latitude: { decimal_places: 7, max_digits: 10 },
  longitude: { decimal_places: 7, max_digits: 10 },
  requestingLoanAmount: { min_value: 0, decimal_places: 2, max_digits: 12 },
  loanTermMonths: { min_value: 1, decimal_places: 0, max_value: 360 },
  personalCapitalPercentage: {
    min_value: 0,
    max_value: 100.0,
    decimal_places: 2,
  },
  monthsInPeriod: { min_value: 1, max_value: 12, decimal_places: 0 },
  totalRevenue: { min_value: 0, decimal_places: 2, max_digits: 12 },
  netProfit: { min_value: 0, decimal_places: 2, max_digits: 12 },
  depreciation: { min_value: 0, decimal_places: 2, max_digits: 12 },
  totalAssets: { min_value: 0, decimal_places: 2, max_digits: 12 },
  cashEquivalents: { min_value: 0, decimal_places: 2, max_digits: 12 },
  tradeReceivables: { min_value: 0, decimal_places: 2, max_digits: 12 },
  inventoryValue: { min_value: 0, decimal_places: 2, max_digits: 12 },
  totalCurrentLiabilities: {
    min_value: 0,
    decimal_places: 2,
    max_digits: 12,
  },
  shareholderEquity: { min_value: 0, decimal_places: 2, max_digits: 12 },
  alternativeNetWorth: { min_value: 0, decimal_places: 2, max_digits: 12 },
  outstandingLoans: { min_value: 0, decimal_places: 2, max_digits: 12 },
  approvedCreditLimit: { min_value: 0, decimal_places: 2, max_digits: 12 },
  monthlyDebtService: { min_value: 0, decimal_places: 2, max_digits: 12 },
  personalIncome: { min_value: 0, decimal_places: 2, max_digits: 12 },
  personalNetWorth: { min_value: 0, decimal_places: 2, max_digits: 12 },
  personalLoans: { min_value: 0, decimal_places: 2, max_digits: 12 },
  loanMinAmount: { min_value: 0, decimal_places: 2, max_digits: 12 },
  loanMaxAmount: { min_value: 0, decimal_places: 2, max_digits: 12 },
  solarLoanMinAmount: { min_value: 0, decimal_places: 2, max_digits: 12 },
  solarLoanMaxAmount: { min_value: 0, decimal_places: 2, max_digits: 12 },
  batteryLoanMinAmount: { min_value: 0, decimal_places: 2, max_digits: 12 },
  batteryLoanMaxAmount: { min_value: 0, decimal_places: 2, max_digits: 12 },
  evLoanMinAmount: { min_value: 0, decimal_places: 2, max_digits: 12 },
  evLoanMaxAmount: { min_value: 0, decimal_places: 2, max_digits: 12 },
};

@Injectable({
  providedIn: 'root',
})
export class NumberValidatorService {
  numberValidator(keyOrConstraints: string | NumberConstraints): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const val = control.value;
      if (val === null || val === undefined || val === '') {
        return null;
      }

      let constraints: NumberConstraints;
      if (typeof keyOrConstraints === 'string') {
        constraints = NUMERIC_FIELD_CONSTRAINTS[keyOrConstraints] || {};
      } else {
        constraints = keyOrConstraints;
      }

      const numStr = String(val);
      if (isNaN(Number(numStr))) {
        return { invalidNumber: true };
      }

      const num = Number(numStr);

      if (
        constraints.min_value !== undefined &&
        constraints.min_value !== null
      ) {
        if (num < constraints.min_value) {
          return { min: { min: constraints.min_value, actual: num } };
        }
      }

      if (
        constraints.max_value !== undefined &&
        constraints.max_value !== null
      ) {
        if (num > constraints.max_value) {
          return { max: { max: constraints.max_value, actual: num } };
        }
      }

      const parts = numStr.split('.');
      const integerPart = parts[0].replace(/^-/, '');
      const decimalPart = parts[1] || '';

      if (
        constraints.decimal_places !== undefined &&
        constraints.decimal_places !== null
      ) {
        if (constraints.decimal_places === 0 && decimalPart.length > 0) {
          return { pattern: true };
        }
        if (decimalPart.length > constraints.decimal_places) {
          return {
            decimalPlaces: {
              max: constraints.decimal_places,
              actual: decimalPart.length,
            },
          };
        }
      }

      if (
        constraints.max_digits !== undefined &&
        constraints.max_digits !== null
      ) {
        if (
          constraints.decimal_places !== undefined &&
          constraints.decimal_places !== null &&
          constraints.decimal_places > 0
        ) {
          const maxWholeDigits =
            constraints.max_digits - constraints.decimal_places;
          if (integerPart.length > maxWholeDigits) {
            return {
              maxDigits: {
                max: constraints.max_digits,
                actual: integerPart.length + decimalPart.length,
                maxWholeDigits: maxWholeDigits,
                actualWholeDigits: integerPart.length,
                isWholeDigits: true,
              },
            };
          }
        } else {
          if (integerPart.length > constraints.max_digits) {
            return {
              maxDigits: {
                max: constraints.max_digits,
                actual: integerPart.length,
              },
            };
          }
        }
      }
      return null;
    };
  }
}
